import { Alert, AlertDescription, AlertIcon, Box, Checkbox, Flex, Image, Input, Text } from '@chakra-ui/react'
import { useState } from 'react'

import { FileInput } from '../components/FileInput'
import { FormField } from '../components/FormField'
import { FormModal } from '../components/FormModal'
import { MaskedInput, MoneyInput } from '../components/MaskedInput'
import { FormSelect } from '../components/Select'
import { useAppToast } from '../helpers'
import ContestsService from '../services/Contests/ContestsService'
import { IPostAuctionSalePayload } from '../services/Contests/interfaces'
import { LiveArtwork } from '../services/Event/types'

// PostAuctionSaleController: `receipt => file|mimes:jpeg,jpg,png,pdf|max:10240`.
const RECEIPT_MAX_MB = 10

interface IPostAuctionSaleModalProps {
  isOpen: boolean
  onClose: () => void
  artwork: LiveArtwork
  onSaleRegistered: () => void
}

export default function PostAuctionSaleModal({
  isOpen,
  onClose,
  artwork,
  onSaleRegistered
}: IPostAuctionSaleModalProps) {
  const toast = useAppToast()
  const initialSalePrice = parseFloat(artwork.starting_bid) > 0 ? parseFloat(artwork.starting_bid) : undefined
  const [form, setForm] = useState<IPostAuctionSalePayload>({
    buyer_name: '',
    buyer_email: '',
    buyer_phone: '',
    buyer_cpf: '',
    payment_type: 'pix',
    sale_price: initialSalePrice
  })
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [acknowledgedUnlistedArtwork, setAcknowledgedUnlistedArtwork] = useState(false)
  const contestsService = new ContestsService()

  const handleClose = () => {
    setForm({
      buyer_name: '',
      buyer_email: '',
      buyer_phone: '',
      buyer_cpf: '',
      payment_type: 'pix',
      sale_price: initialSalePrice
    })
    setReceiptFile(null)
    setAcknowledgedUnlistedArtwork(false)
    onClose()
  }

  const setField = (field: keyof IPostAuctionSalePayload, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    try {
      const formData = new FormData()
      formData.append('buyer_name', form.buyer_name)
      formData.append('buyer_email', form.buyer_email)
      formData.append('buyer_phone', form.buyer_phone)
      formData.append('buyer_cpf', form.buyer_cpf)
      formData.append('payment_type', form.payment_type)
      if (form.sale_price) {
        formData.append('sale_price', form.sale_price.toString())
      }
      if (receiptFile) {
        formData.append('receipt', receiptFile)
      }

      await contestsService.registerPostAuctionSale(artwork.id, formData)
      toast.success('Venda registrada', 'A obra foi marcada como vendida e o financeiro foi notificado.')
      handleClose()
      onSaleRegistered()
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { message?: string } } }
      toast.error('Erro ao registrar venda', apiErr)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Registrar venda pós-leilão"
      onSubmit={handleSubmit}
      confirmLabel="Registrar venda"
      isLoading={isSubmitting}
      isConfirmDisabled={
        !form.buyer_name ||
        !form.buyer_email ||
        !form.buyer_phone ||
        !form.buyer_cpf ||
        !receiptFile ||
        !form.sale_price ||
        form.sale_price <= 0 ||
        (!artwork.available_for_auction && !acknowledgedUnlistedArtwork)
      }
    >
      <Flex align="center" gap={3} mb={5}>
        {artwork.art_image_url ? (
          <Image
            src={artwork.art_image_url}
            alt={artwork.name}
            boxSize="72px"
            objectFit="cover"
            borderRadius="md"
            flexShrink={0}
          />
        ) : (
          <Box boxSize="72px" bg="gray.100" borderRadius="md" flexShrink={0} />
        )}
        <Box>
          <Text fontWeight="semibold" fontSize="sm">
            {artwork.name}
          </Text>
          <Text variant="caption">{artwork.author_name}</Text>
        </Box>
      </Flex>

      {/* Buyer section */}
      <Text fontWeight="semibold" fontSize="sm" mb={3} color="gray.700">
        Comprador
      </Text>

      <Flex direction="column" gap={3} mb={5}>
        <FormField label="Nome Completo" isRequired>
          <Input
            size="sm"
            value={form.buyer_name}
            onChange={(e) => setField('buyer_name', e.target.value)}
            placeholder="Nome do comprador"
          />
        </FormField>

        <FormField label="E-mail" isRequired>
          <Input
            size="sm"
            type="email"
            value={form.buyer_email}
            onChange={(e) => setField('buyer_email', e.target.value)}
            placeholder="email@exemplo.com"
          />
        </FormField>

        <Flex gap={3}>
          <FormField label="Telefone" isRequired flex={1}>
            <MaskedInput
              mask="phone"
              size="sm"
              value={form.buyer_phone}
              onChange={(phone) => setField('buyer_phone', phone)}
            />
          </FormField>

          <FormField label="CPF" isRequired flex={1}>
            <MaskedInput mask="cpf" size="sm" value={form.buyer_cpf} onChange={(cpf) => setField('buyer_cpf', cpf)} />
          </FormField>
        </Flex>
      </Flex>

      {/* Sale data section */}
      <Text fontWeight="semibold" fontSize="sm" mb={3} color="gray.700">
        Dados da venda
      </Text>

      <Flex direction="column" gap={3}>
        <FormField label="Valor de venda" isRequired>
          <MoneyInput
            size="sm"
            value={form.sale_price ?? null}
            onChange={(sale_price) => setForm((prev) => ({ ...prev, sale_price: sale_price ?? undefined }))}
          />
        </FormField>
        <FormField label="Tipo de pagamento" isRequired>
          <FormSelect
            size="sm"
            value={form.payment_type}
            onChange={(value) => setField('payment_type', value as IPostAuctionSalePayload['payment_type'])}
            options={[
              { value: 'pix', label: 'Pix' },
              { value: 'credit', label: 'Crédito' },
              { value: 'debit', label: 'Débito' }
            ]}
            hideEmptyOption
          />
        </FormField>

        <FileInput
          accept="receipt"
          maxSizeMB={RECEIPT_MAX_MB}
          label="Comprovante"
          isRequired
          preview="thumbnail"
          value={receiptFile}
          onChange={setReceiptFile}
        />
      </Flex>

      {/* Warning for artworks not made available for auction */}
      {!artwork.available_for_auction && (
        <Flex direction="column" gap={3} mt={4}>
          <Alert status="warning" borderRadius="md" fontSize="sm">
            <AlertIcon />
            <AlertDescription>
              Esta obra não foi disponibilizada pelo artista para leilão. Você tem certeza que deseja registrar esta
              venda?
            </AlertDescription>
          </Alert>
          <Checkbox
            isChecked={acknowledgedUnlistedArtwork}
            onChange={(e) => setAcknowledgedUnlistedArtwork(e.target.checked)}
          >
            <Text fontSize="sm">Confirmo que desejo registrar a venda desta obra não disponibilizada</Text>
          </Checkbox>
        </Flex>
      )}
    </FormModal>
  )
}
